/** Sanitize a numeric draft: no negatives, no leading zeros (`04` → `4`), empty/`.` allowed while typing. */
export function parseNonNegativeNumber(
  raw: string,
  opts?: { integer?: boolean; decimalPlaces?: number; maxDigits?: number },
): string {
  const integer = Boolean(opts?.integer);
  const decimalPlaces = opts?.decimalPlaces;
  const maxDigits = opts?.maxDigits;
  let cleaned = raw.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (integer) {
    cleaned = dot === -1 ? cleaned : cleaned.slice(0, dot);
  } else if (dot !== -1) {
    let decPart = cleaned.slice(dot + 1).replace(/\./g, '');
    if (decimalPlaces != null) decPart = decPart.slice(0, decimalPlaces);
    cleaned = `${cleaned.slice(0, dot + 1)}${decPart}`;
  }
  if (cleaned === '') return '';
  if (cleaned === '.') return integer ? '' : '.';

  if (integer) {
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return '0';
    let out = String(n);
    if (maxDigits != null && maxDigits > 0) out = out.slice(0, maxDigits);
    return out;
  }

  const hasDot = cleaned.includes('.');
  const [intPart, decPart = ''] = cleaned.split('.');
  let strippedInt = intPart.replace(/^0+(?=\d)/, '');
  if (maxDigits != null && maxDigits > 0) strippedInt = strippedInt.slice(0, maxDigits);
  const intOut = strippedInt === '' ? '0' : strippedInt;
  if (hasDot) return `${intOut}.${decPart}`;
  return intOut;
}

export function roundToDecimalPlaces(value: number, decimalPlaces: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
}

/** Fixed-width display for committed numeric field values. */
export function formatNonNegativeDisplay(
  value: number | string | null | undefined,
  opts?: { integer?: boolean; decimalPlaces?: number },
): string {
  if (value === '' || value == null) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (opts?.integer) return String(Math.round(n));
  if (opts?.decimalPlaces != null) {
    return roundToDecimalPlaces(n, opts.decimalPlaces).toFixed(opts.decimalPlaces);
  }
  return String(n);
}

export function commitNonNegativeNumber(
  draft: string,
  opts?: { integer?: boolean; min?: number; max?: number; decimalPlaces?: number },
): number {
  const fallback = opts?.min ?? 0;
  if (draft === '' || draft === '.') return fallback;
  let n = opts?.integer ? Number.parseInt(draft, 10) : Number(draft);
  if (!Number.isFinite(n) || n < 0) return fallback;
  if (opts?.decimalPlaces != null) {
    const factor = 10 ** opts.decimalPlaces;
    n = Math.round(n * factor) / factor;
  }
  if (opts?.min != null && n < opts.min) return opts.min;
  if (opts?.max != null && n > opts.max) return opts.max;
  return n;
}
